// src/pages/WhyNewAudiaPage.jsx
// Public page explaining why NewAudia - AI/SEO discovery
import React from 'react';
import { Link } from 'react-router-dom';
import UniversalFooter from '../components/UniversalFooter';
import { Seo, PAGE_SEO } from '../components/Seo';

const WhyNewAudiaPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Seo {...PAGE_SEO.whyNewAudia} />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-white mb-6">Why NewAudia</h1>

          <p className="text-lg text-slate-300 leading-relaxed">
            Audiobooks have made books more accessible, but accessibility alone
            does not equal usability.
            Many readers discover that traditional audiobooks work well for
            passive listening yet fall short when the goal is learning,
            reference, or deeper understanding.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">The Limits of Traditional Audiobooks</h2>
          <p className="text-slate-300">
            Most audiobooks are designed as linear recordings.
            Once playback begins, the listener is expected to move forward
            sequentially, regardless of intent.
          </p>
          <p className="text-slate-300 mt-4">
            This model creates friction for readers who want to:
          </p>
          <ul className="space-y-3 text-slate-300 mt-4">
            <li>Revisit specific ideas or sections</li>
            <li>Use audio for learning or professional development</li>
            <li>Reference material efficiently</li>
            <li>Engage with complex or non-fiction content</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Audio Should Preserve a Book's Value</h2>
          <p className="text-slate-300">
            When a book is converted to audio, much of its structure is often lost.
            Tables of contents, logical divisions, and conceptual groupings
            become difficult to access.
          </p>
          <p className="text-slate-300 mt-4">
            As a result, many nonfiction and educational works lose practical value
            when consumed as traditional audiobooks.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">A Different Approach</h2>
          <p className="text-slate-300">
            NewAudia approaches books as structured systems of ideas rather than
            as uninterrupted audio streams.
            The platform is designed to support navigation, exploration,
            and interaction while respecting the author's intent.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Designed for Intent-Driven Readers</h2>
          <p className="text-slate-300">
            Not all listening is casual.
            Many readers come to audio with a specific purpose—learning a concept,
            solving a problem, or revisiting an important idea.
          </p>
          <p className="text-slate-300 mt-4">
            NewAudia supports intent-driven consumption by allowing readers to
            engage with books in ways that align with their goals,
            rather than forcing linear playback.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Better Alignment Between Readers, Authors, and Publishers</h2>
          <p className="text-slate-300">
            NewAudia creates alignment between all participants in the reading
            ecosystem:
          </p>
          <ul className="space-y-3 text-slate-300 mt-4">
            <li><strong className="text-slate-100">Readers</strong> gain clarity, control, and usability</li>
            <li><strong className="text-slate-100">Authors</strong> preserve the value of their ideas in audio form</li>
            <li><strong className="text-slate-100">Publishers</strong> gain access to modern distribution and engagement models</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Not a Replacement—An Evolution</h2>
          <p className="text-slate-300">
            NewAudia does not attempt to replace traditional audiobooks.
            Instead, it evolves the format for use cases where linear audio
            is insufficient.
          </p>
          <p className="text-slate-300 mt-4">
            The result is an audiobook experience designed for understanding,
            retention, and meaningful engagement.
          </p>

          <p className="text-slate-300 mt-6">
            Learn more at <a href="https://www.newaudia.com/" className="text-indigo-400 hover:text-indigo-300 underline">https://www.newaudia.com/</a>
          </p>

          <div className="mt-10 pt-6 border-t border-slate-800">
            <p className="text-slate-400 text-sm">Related pages:</p>
            <ul className="flex flex-wrap gap-4 mt-2">
              <li><Link to="/how-it-works" className="text-indigo-400 hover:text-indigo-300 underline text-sm">How It Works</Link></li>
              <li><Link to="/for-authors" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Authors</Link></li>
              <li><Link to="/for-publishers" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Publishers</Link></li>
              <li><Link to="/ai" className="text-indigo-400 hover:text-indigo-300 underline text-sm">AI Discovery</Link></li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 mt-12 italic">Last updated: 2026-01-08</p>
        </article>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default WhyNewAudiaPage;
